(function () {
  /**
   * Mobile menu max-height fix
   * -----------------------------------------
   * Problem:
   * - Top-level mobile dropdowns (.mobile-menu-item.as-tab) animate open via max-height.
   * - Nested submenus toggle visibility inside them.
   * - When nested submenus open/close, the parent max-height is not recalculated -> content clips.
   *
   * Fix:
   * - Recalculate the correct max-height for the containing .mobile-menu-item.as-tab whenever:
   *   - A top-level expand button is clicked
   *   - A nested submenu toggle/trigger is clicked
   *   - Submenu content visibility changes programmatically (MutationObserver)
   *   - Window resizes
   */

  const SELECTORS = {
    mobileMenuRoot: "mobile-menu",
    topLevelItem: ".mobile-menu-item.as-tab",
    topLevelButton: ".mobile-menu-expand-btn",
    submenuToggle: "[data-mobile-submenu-toggle]",
    submenuTrigger: "[data-mobile-submenu-trigger]",
    submenuContent: "[data-mobile-submenu-content]",
  };

  /** Match header mobile breakpoint (see MobileMenu in pg-header-component.js). */
  const MOBILE_MENU_MQ = "(max-width: 1199.98px)";

  const raf2 = (fn) => {
    requestAnimationFrame(() => requestAnimationFrame(fn));
  };

  const TRANSITION_FALLBACK_MS = 350;

  function isElement(node) {
    return node && node.nodeType === 1;
  }

  // Track per-submenu animation state so repeated toggles don't get "stuck"
  // (e.g. multiple transitionend listeners fighting each other).
  const submenuAnimationState = new WeakMap();

  function stopSubmenuAnimation(content) {
    const state = submenuAnimationState.get(content);
    if (!state) return;

    if (state.onEnd) {
      content.removeEventListener("transitionend", state.onEnd);
    }
    if (state.rafId) {
      cancelAnimationFrame(state.rafId);
    }
    if (state.fallbackId) {
      clearTimeout(state.fallbackId);
    }

    submenuAnimationState.delete(content);
  }

  function startParentTracking(menuItem, content) {
    if (!menuItem) return;

    const state = submenuAnimationState.get(content) || {};
    // Update parent max-height continuously during the nested transition so
    // `.mobile-menu-item.as-tab` never clips.
    const tick = () => {
      setMenuItemMaxHeight(menuItem);
      state.rafId = requestAnimationFrame(tick);
      submenuAnimationState.set(content, state);
    };

    state.rafId = requestAnimationFrame(tick);
    submenuAnimationState.set(content, state);
  }

  function stopParentTracking(content) {
    const state = submenuAnimationState.get(content);
    if (!state || !state.rafId) return;
    cancelAnimationFrame(state.rafId);
    state.rafId = null;
    submenuAnimationState.set(content, state);
  }

  function getSubmenuParts(fromEl) {
    const submenu = fromEl.closest("[data-mobile-submenu]");
    if (!submenu) return null;

    const content = submenu.querySelector(SELECTORS.submenuContent);
    if (!content) return null;

    const toggleBtn = submenu.querySelector(SELECTORS.submenuToggle);

    return { submenu, content, toggleBtn };
  }

  function openNestedSubmenu({ submenu, content, toggleBtn }) {
    stopSubmenuAnimation(content);

    submenu.classList.add("open");
    if (toggleBtn) toggleBtn.setAttribute("aria-expanded", "true");

    // 1) Make it measurable immediately
    content.hidden = false;
    content.setAttribute("aria-hidden", "false");
    content.style.paddingTop = "";
    content.style.paddingBottom = "";
    content.style.marginTop = "";

    // 2) Start from 0px, then animate to scrollHeight
    content.style.maxHeight = "0px";

    const menuItem = submenu.closest(SELECTORS.topLevelItem);

    raf2(() => {
      const target = content.scrollHeight;
      content.style.maxHeight = `${target}px`;

      // Keep parent `.as-tab` max-height in sync throughout the transition
      startParentTracking(menuItem, content);
    });

    const onEnd = (e) => {
      if (e.target !== content) return;
      if (e.propertyName && e.propertyName !== "max-height") return;

      stopParentTracking(content);
      content.removeEventListener("transitionend", onEnd);

      // Allow content to size naturally once fully open
      content.style.maxHeight = "none";

      // Ensure parent finishes at the correct height
      setMenuItemMaxHeight(menuItem);

      const state = submenuAnimationState.get(content) || {};
      state.onEnd = null;
      submenuAnimationState.set(content, state);
    };

    const state = submenuAnimationState.get(content) || {};
    state.onEnd = onEnd;
    submenuAnimationState.set(content, state);

    content.addEventListener("transitionend", onEnd);
  }

  function closeNestedSubmenu({ submenu, content, toggleBtn }) {
    stopSubmenuAnimation(content);

    // Instant close: remove open state and hide content without animation.
    submenu.classList.remove("open");
    if (toggleBtn) toggleBtn.setAttribute("aria-expanded", "false");

    stopParentTracking(content);
    content.hidden = true;
    content.setAttribute("aria-hidden", "true");
    content.style.maxHeight = "";
    content.style.paddingTop = "";
    content.style.paddingBottom = "";
    content.style.marginTop = "";

    const menuItem = submenu.closest(SELECTORS.topLevelItem);
    setMenuItemMaxHeight(menuItem);
  }

  function toggleNestedSubmenu(fromEl) {
    const parts = getSubmenuParts(fromEl);
    if (!parts) return;

    const { submenu } = parts;
    const isOpen = submenu.classList.contains("open");

    if (isOpen) {
      closeNestedSubmenu(parts);
    } else {
      openNestedSubmenu(parts);
    }

    // Also schedule the existing parent re-measure (in addition to rAF tracking)
    recalcForNode(parts.content);
  }

  function syncInitialNestedState(root) {
    if (!root) return;

    root.querySelectorAll("[data-mobile-submenu]").forEach((submenu) => {
      const content = submenu.querySelector(SELECTORS.submenuContent);
      if (!content) return;

      const toggleBtn = submenu.querySelector(SELECTORS.submenuToggle);
      const expanded =
        (toggleBtn && toggleBtn.getAttribute("aria-expanded") === "true") ||
        !content.hasAttribute("hidden");

      if (expanded) {
        submenu.classList.add("open");
        content.hidden = false;
        content.setAttribute("aria-hidden", "false");
        content.style.maxHeight = "none";
      } else {
        submenu.classList.remove("open");
        content.setAttribute("aria-hidden", "true");
        content.style.maxHeight = "";
      }
    });
  }

  function getCollapsedHeightPx(menuItem) {
    if (!menuItem) return 30;
    const btn = menuItem.querySelector(SELECTORS.topLevelButton);
    // Use actual rendered height so we match theme CSS (incl. padding/border)
    return btn ? btn.offsetHeight : 30;
  }

  function setMenuItemMaxHeight(menuItem) {
    if (!menuItem) return;

    const isActive = menuItem.classList.contains("active");

    if (!isActive) {
      menuItem.style.maxHeight = `${getCollapsedHeightPx(menuItem)}px`;
      return;
    }

    // Ensure max-height reflects CURRENT content (including nested submenus state)
    // scrollHeight returns full content height even if overflow:hidden clips it.
    const targetHeight = menuItem.scrollHeight;
    menuItem.style.maxHeight = `${targetHeight}px`;
  }

  /**
   * Multi-open top-level toggle (independent expand/collapse)
   * --------------------------------------------------------
   * Theme behavior (in pg-header-component) enforces single-open by closing siblings.
   * We intercept the click in capture phase and implement the toggle ourselves so
   * multiple `.mobile-menu-item.as-tab` can remain open simultaneously.
   */
  function toggleTopLevelMenu(fromEl) {
    const btn = fromEl && fromEl.closest ? fromEl.closest(SELECTORS.topLevelButton) : null;
    if (!btn) return;

    const menuItem = btn.closest(SELECTORS.topLevelItem);
    if (!menuItem) return;

    const newState = !menuItem.classList.contains("active");
    menuItem.classList.toggle("active", newState);

    // A11y: theme markup doesn't currently include this, but adding it is safe and helpful.
    btn.setAttribute("aria-expanded", newState ? "true" : "false");

    // Apply the correct max-height immediately (CSS transition handles animation).
    setMenuItemMaxHeight(menuItem);

    // Ensure any other open items also end up at correct heights (no state changes).
    const root = btn.closest(SELECTORS.mobileMenuRoot) || document;
    raf2(() => recalcAllInRoot(root));
  }

  function recalcForNode(node) {
    if (!isElement(node)) return;

    const menuItem = node.closest(SELECTORS.topLevelItem);
    if (!menuItem) return;

    // Recalc after DOM changes have applied (hidden attr toggled, class changes, etc.)
    raf2(() => setMenuItemMaxHeight(menuItem));
  }

  function recalcAllInRoot(root) {
    if (!root) return;
    root.querySelectorAll(SELECTORS.topLevelItem).forEach((item) => {
      raf2(() => setMenuItemMaxHeight(item));
    });
  }

  function observeSubmenuChanges(root) {
    if (!root) return;

    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        const target = m.target;
        if (!isElement(target)) continue;

        // When submenu content is shown/hidden programmatically, ensure parent grows/shrinks.
        if (target.matches(SELECTORS.submenuContent)) {
          recalcForNode(target);
          continue;
        }

        // Some themes toggle "open" class on the submenu wrapper (also affects height).
        if (
          target.matches("[data-mobile-submenu]") &&
          m.attributeName === "class"
        ) {
          recalcForNode(target);
        }
      }
    });

    observer.observe(root, {
      subtree: true,
      attributes: true,
      attributeFilter: ["hidden", "class", "aria-hidden", "aria-expanded"],
    });
  }

  function init() {
    // Capture-phase handler for nested submenu toggles:
    // We need to prevent the theme from immediately applying `[hidden]` on close,
    // otherwise there's no time to animate the max-height transition.
    document.addEventListener(
      "click",
      (event) => {
        const target = event.target;
        if (!isElement(target)) return;

        const submenuToggle = target.closest(SELECTORS.submenuToggle);
        const submenuTrigger = target.closest(SELECTORS.submenuTrigger);

        const control = submenuToggle || submenuTrigger;
        if (!control) return;

        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        toggleNestedSubmenu(control);
      },
      true
    );

    // Capture-phase handler for TOP-LEVEL expand buttons:
    // Prevent the theme's `toggleExpandMenu()` from closing other siblings.
    document.addEventListener(
      "click",
      (event) => {
        const target = event.target;
        if (!isElement(target)) return;

        const topBtn = target.closest(SELECTORS.topLevelButton);
        if (!topBtn) return;

        // Only intercept top-level dropdowns (not plain link items).
        const menuItem = topBtn.closest(SELECTORS.topLevelItem);
        if (!menuItem) return;

        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        toggleTopLevelMenu(topBtn);
      },
      true
    );

    // Bubble-phase handler for menu open/close controls:
    // run AFTER existing component listeners so we can re-measure final heights.
    document.addEventListener("click", (event) => {
      const target = event.target;
      if (!isElement(target)) return;

      // Menu open/close buttons can trigger programmatic submenu closing; recalc afterwards.
      const maybeMenuControl = target.closest(".menu-open-btn, .menu-close-btn");
      if (maybeMenuControl) {
        const root = maybeMenuControl.closest(SELECTORS.mobileMenuRoot) || document;
        raf2(() => recalcAllInRoot(root));
      }
    });

    // Keep heights correct on resize only while mobile menu breakpoint is active.
    // Avoids measuring hidden desktop layout (display:none / zero boxes) and stale inline max-heights.
    window.addEventListener("resize", () => {
      if (!window.matchMedia(MOBILE_MENU_MQ).matches) return;
      document
        .querySelectorAll(SELECTORS.mobileMenuRoot)
        .forEach((root) => recalcAllInRoot(root));
    });

    // Initial pass (helps if menu items are active from previous navigation state)
    document
      .querySelectorAll(SELECTORS.mobileMenuRoot)
      .forEach((root) => {
        syncInitialNestedState(root);

        // If any top-level items are pre-open, reflect that on aria-expanded.
        root.querySelectorAll(SELECTORS.topLevelItem).forEach((item) => {
          const btn = item.querySelector(SELECTORS.topLevelButton);
          if (!btn) return;
          btn.setAttribute(
            "aria-expanded",
            item.classList.contains("active") ? "true" : "false"
          );
        });

        if (window.matchMedia(MOBILE_MENU_MQ).matches) {
          recalcAllInRoot(root);
        }
        observeSubmenuChanges(root);
      });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
